import React from 'react';

import { AlineTransforms } from '@aline/common';
import type { Editor } from 'slate';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { createPublishedTabScopedRegistry } from 'src/editor/tab-scoped-registry';

type AlineTransformsType = typeof AlineTransforms;

export type WrappedTransforms = {
  [K in keyof AlineTransformsType]: (
    ...args: Parameters<AlineTransformsType[K]>
  ) => ReturnType<AlineTransformsType[K]>;
};

const wrappedTransformsByEditor = new WeakMap<Editor, WrappedTransforms>();
const authorizedTransformsRegistry = createPublishedTabScopedRegistry<WrappedTransforms>({
  useTabIdErrorMessage: 'useAuthorizedTransforms must be used within an editor runtime.',
});
const PublishedAuthorizedTransformsProvider = authorizedTransformsRegistry.Provider;

export function getAuthorizedTransformsForEditor(editor: Editor): WrappedTransforms {
  return wrappedTransformsByEditor.get(editor) ?? AlineTransforms;
}

export function AuthorizedTransformsProvider({
  AuthorizedTransforms,
  children,
  tabId,
}: {
  AuthorizedTransforms: WrappedTransforms;
  tabId: UUID;
  children?: React.ReactNode;
}): JSX.Element {
  return React.createElement(
    PublishedAuthorizedTransformsProvider,
    { state: AuthorizedTransforms, tabId },
    children
  );
}

export function useAuthorizedTransforms(): {
  AuthorizedTransforms: WrappedTransforms;
} {
  const runtime = useMaybeEditorRuntime();
  const AuthorizedTransforms = authorizedTransformsRegistry.useSelectorForTabId(
    runtime?.tabId ?? null,
    transforms => transforms ?? AlineTransforms
  );

  return { AuthorizedTransforms };
}

export function storeAuthorizedTransformsForEditor(
  editor: Editor,
  wrappedTransforms: WrappedTransforms
): void {
  wrappedTransformsByEditor.set(editor, wrappedTransforms);
}

export function deleteAuthorizedTransformsForEditor(editor: Editor): void {
  wrappedTransformsByEditor.delete(editor);
}
